import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Car, Star, DollarSign, Users, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { fmtMoney, fmtDate } from "@/lib/format";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/stats")
      .then((r) => setStats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-24" data-testid="admin-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#FF6B00]" />
      </div>
    );
  }

  const cards = [
    { icon: Car, label: "Available Vehicles", value: stats?.available_vehicles ?? 0, testid: "metric-available" },
    { icon: Star, label: "Featured Vehicles", value: stats?.featured_vehicles ?? 0, testid: "metric-featured" },
    { icon: DollarSign, label: "Inventory Value", value: fmtMoney(stats?.inventory_value), testid: "metric-value" },
    { icon: Users, label: "New Leads Today", value: stats?.new_leads_today ?? 0, testid: "metric-leads-today" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-display text-2xl font-black uppercase text-zinc-900 mb-6">Dashboard</h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-8" data-testid="admin-metrics">
        {cards.map((c) => (
          <div key={c.testid} className="bg-white border border-zinc-200 p-5" data-testid={c.testid}>
            <c.icon className="h-5 w-5 text-[#FF6B00] mb-3" />
            <p className="font-display text-3xl font-black text-zinc-900">{c.value}</p>
            <p className="text-xs font-mono uppercase tracking-wider text-zinc-500 mt-1">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <div className="bg-white border border-zinc-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-base font-bold uppercase text-zinc-900">Recent Leads</h2>
            <Link to="/admin/leads" className="text-xs font-bold uppercase tracking-wider text-[#FF6B00] hover:text-[#FF8533]" data-testid="dashboard-all-leads">
              View All
            </Link>
          </div>
          {stats?.recent_leads?.length ? (
            <div className="space-y-3" data-testid="dashboard-recent-leads">
              {stats.recent_leads.map((l) => (
                <div key={l.id} className="flex items-center justify-between gap-3 border-b border-zinc-200 pb-3 text-sm">
                  <div className="min-w-0">
                    <p className="font-bold text-zinc-900 truncate">{l.name}</p>
                    <p className="text-xs text-zinc-500 truncate">{l.vehicle_title || l.lead_type}</p>
                  </div>
                  <span className="text-xs text-zinc-500 shrink-0">{fmtDate(l.created_at)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-zinc-500" data-testid="dashboard-no-leads">No leads yet.</p>
          )}
        </div>

        <div className="bg-white border border-zinc-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-base font-bold uppercase text-zinc-900">Recent Inventory</h2>
            <Link to="/admin/inventory" className="text-xs font-bold uppercase tracking-wider text-[#FF6B00] hover:text-[#FF8533]" data-testid="dashboard-all-inventory">
              View All
            </Link>
          </div>
          {stats?.recent_vehicles?.length ? (
            <div className="space-y-3" data-testid="dashboard-recent-vehicles">
              {stats.recent_vehicles.map((v) => (
                <div key={v.id} className="flex items-center justify-between gap-3 border-b border-zinc-200 pb-3 text-sm">
                  <p className="font-bold text-zinc-900 truncate">{v.title}</p>
                  <span className="text-[#FF6B00] font-bold shrink-0">{fmtMoney(v.price)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-zinc-500">No vehicles yet.</p>
          )}
        </div>
      </div>
    </div>
  );
}
